/**
 * Mod management — streamer-only. Grant mod access by Twitch/YouTube/Discord
 * handle (stored as a pending invite until that person logs in), list current
 * members + pending invites, and revoke either. Discord is login-only for
 * mods (see IdentityPlatform in api/src/lib/keys.ts) — it's not offered as a
 * streamer sign-in option elsewhere in the app.
 *
 * Also hosts the "End broadcast" control: ending the live session closes it, and
 * the next activity opens a fresh one (carrying unplayed requests over).
 */
import { useCallback, useEffect, useState } from "react";
import { api, type ChannelMembers } from "../lib/api";

type ModPlatform = "twitch" | "youtube" | "discord";

export function ModsPanel({ channelId }: { channelId: string }) {
  const [data, setData] = useState<ChannelMembers | null>(null);
  const [platform, setPlatform] = useState<ModPlatform>("twitch");
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!channelId) return;
    try {
      setData(await api.channelMembers(channelId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [channelId]);

  // Drop the previous channel's mod list on switch rather than leaving it on
  // screen while the new one loads.
  useEffect(() => {
    setData(null);
  }, [channelId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!handle.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.grantMod(channelId, platform, handle.trim());
      setHandle("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function revokeMember(accountId: string) {
    if (!window.confirm("Remove this mod's access?")) return;
    await api.revokeMod(channelId, { accountId });
    await refresh();
  }
  async function revokeInvite(p: ModPlatform, h: string) {
    await api.revokeMod(channelId, { platform: p, handle: h });
    await refresh();
  }

  async function endBroadcast() {
    if (!window.confirm("End the current broadcast? Unplayed requests carry over.")) return;
    setBusy(true);
    try {
      await api.endBroadcast(channelId);
    } finally {
      setBusy(false);
    }
  }

  // The owner's own streamer membership isn't shown as a removable "mod".
  const mods = data?.members.filter((m) => m.role === "mod") ?? [];

  return (
    <>
      <div className="panel">
        <h2>Mods</h2>
        <p className="muted panel-lead">
          Grant another Twitch, YouTube, or Discord user access to run this
          channel's queue. They can do everything except manage mods, toggle
          chat, or delete the channel.
        </p>

        <form className="row" onSubmit={add} style={{ alignItems: "flex-end" }}>
          <div className="field" style={{ flex: "0 0 140px" }}>
            <label htmlFor="mod-platform">Platform</label>
            <select
              id="mod-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as ModPlatform)}
            >
              <option value="twitch">Twitch</option>
              <option value="youtube">YouTube</option>
              <option value="discord">Discord</option>
            </select>
          </div>
          <div className="field grow">
            <label htmlFor="mod-handle">
              {platform === "twitch"
                ? "Twitch username"
                : platform === "youtube"
                  ? "Google account email"
                  : "Discord username"}
            </label>
            <input
              id="mod-handle"
              value={handle}
              placeholder={
                platform === "twitch"
                  ? "their twitch login"
                  : platform === "youtube"
                    ? "their google email"
                    : "their discord username"
              }
              spellCheck={false}
              onChange={(e) => setHandle(e.target.value)}
            />
          </div>
          <button className="btn primary row-action" type="submit" disabled={busy || !handle.trim()}>
            Add mod
          </button>
        </form>

        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

        {(mods.length > 0 || (data?.invites.length ?? 0) > 0) && (
          <ul className="connections" style={{ marginTop: 16 }}>
            {mods.map((m) => (
              <li key={m.accountId} className="connection-row">
                <span className="badge">mod</span>
                <div className="connection-detail">
                  <span>{m.displayName}</span>
                </div>
                <button className="btn danger" onClick={() => revokeMember(m.accountId)}>
                  Remove
                </button>
              </li>
            ))}
            {data?.invites.map((i) => (
              <li key={`${i.platform}:${i.handle}`} className="connection-row">
                <span className={`badge platform ${i.platform}`}>{i.platform}</span>
                <div className="connection-detail">
                  <span>{i.handle}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    pending — activates on their first login
                  </span>
                </div>
                <button className="btn" onClick={() => revokeInvite(i.platform, i.handle)}>
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="panel">
        <h2>Broadcast</h2>
        <p className="muted panel-lead">
          Ending a broadcast closes the current session. The next request opens a
          new one and carries any unplayed requests over.
        </p>
        <button className="btn danger" onClick={endBroadcast} disabled={busy}>
          End broadcast
        </button>
      </div>
    </>
  );
}
